// ============================================================
// AsiaStyle Telegram Mini App — Root Component
// Auth: Telegram initData → Workers API /api/mini-app/me
// Theme: Telegram CSS variables + @telegram-apps/sdk-react
// ============================================================

import { AppShell } from "./components/AppShell.js";
import { CrmCommerceApp } from "./components/CrmCommerceApp.js";
import { ErrorState } from "./components/ErrorState.js";
import { LoadingState } from "./components/LoadingState.js";
import {
  CheckoutUiShellApp,
  isCheckoutUiShellEnabled,
} from "./checkout-ui-shell/index.js";
import { LocaleProvider } from "./i18n/LocaleProvider.js";
import { getMessages } from "./i18n/messages.js";
import { useSession } from "./hooks/useSession.js";
import { isMiniAppCrmRuntimeEnabled } from "./lib/crm-feature-flags.js";
import { StoreV2App, isStoreV2Route } from "./store-v2/index.js";

export function App() {
  // FUNCTIONALITY-101: isolated Store V2 route — same-origin static catalog,
  // no /me session gate, no cross-origin catalog fetch. Checked first.
  if (isStoreV2Route()) {
    return (
      <LocaleProvider languageCode="uk">
        <StoreV2App />
      </LocaleProvider>
    );
  }

  // COS-MINIAPP-001: CRM Runtime commerce channel (flag-gated, safe defaults off)
  if (isMiniAppCrmRuntimeEnabled()) {
    return (
      <LocaleProvider languageCode="uk">
        <CrmCommerceApp />
      </LocaleProvider>
    );
  }

  // MINIAPP-064: offline checkout/order UX shell — no live session/API when enabled.
  if (isCheckoutUiShellEnabled()) {
    return (
      <LocaleProvider languageCode="uk">
        <CheckoutUiShellApp />
      </LocaleProvider>
    );
  }

  return <LiveAuthenticatedApp />;
}

function LiveAuthenticatedApp() {
  const { loading, error, session, retry } = useSession();
  const bootstrapMessages = getMessages("uk");

  if (loading) {
    return <LoadingState label={bootstrapMessages.session.connecting} />;
  }

  if (error) {
    return <ErrorState message={error} onRetry={retry} />;
  }

  if (!session) {
    return <ErrorState message={bootstrapMessages.session.unavailable} onRetry={retry} />;
  }

  const displayName =
    session.client?.first_name ??
    session.telegram_user.first_name ??
    bootstrapMessages.common.customerFallback;

  const segment = session.client_profile?.segment ?? null;

  return (
    <LocaleProvider languageCode={session.telegram_user.language_code ?? null}>
      <AppShell displayName={displayName} segment={segment} session={session} />
    </LocaleProvider>
  );
}
